import { differenceInMinutes, isSameDay, parseISO, subDays } from 'date-fns';
import { NotificationItem } from '@/types/notification-types';

export type NotificationGroupKey =
  | 'new'
  | 'earlier-today'
  | 'yesterday'
  | 'older';

export interface NotificationGroup {
  key: NotificationGroupKey;
  label: string;
  items: NotificationItem[];
}

const NEW_THRESHOLD_MINUTES = 60;

const GROUP_ORDER: { key: NotificationGroupKey; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'earlier-today', label: 'Earlier Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'older', label: 'Older' },
];

const resolveGroup = (createdAt: string, now: Date): NotificationGroupKey => {
  const date = parseISO(createdAt);
  if (isSameDay(date, now)) {
    return differenceInMinutes(now, date) <= NEW_THRESHOLD_MINUTES
      ? 'new'
      : 'earlier-today';
  }
  if (isSameDay(date, subDays(now, 1))) {
    return 'yesterday';
  }
  return 'older';
};

export const groupNotificationsByTime = (
  items: NotificationItem[],
  now: Date = new Date(),
): NotificationGroup[] => {
  const buckets = new Map<NotificationGroupKey, NotificationItem[]>();

  for (const item of items) {
    const key = resolveGroup(item.created_at, now);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(item);
    } else {
      buckets.set(key, [item]);
    }
  }

  return GROUP_ORDER.filter((group) => buckets.has(group.key)).map((group) => ({
    key: group.key,
    label: group.label,
    items: buckets.get(group.key)!,
  }));
};
